/**
 * Kullanıcı Rızası (CMP) - AEA, UK, İsviçre uyumu
 * 3 seçenek: İzin ver, İzin verme, Seçenekleri yönet
 */

import React, { useState, useEffect } from 'react';
import {
  hasConsentChoice,
  consentGrantAll,
  consentDenyAll,
  consentUpdate,
  getStoredConsent,
  type ConsentPreferences
} from '../utils/consent';

const BANNER_STYLE: React.CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  padding: '16px 20px',
  background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
  color: '#f1f5f9',
  boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: '14px',
  lineHeight: 1.5
};

const MESSAGE_STYLE: React.CSSProperties = {
  maxWidth: '900px',
  margin: '0 auto 14px',
  textAlign: 'center'
};

const BUTTON_GROUP_STYLE: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  justifyContent: 'center',
  alignItems: 'center'
};

const BUTTON_BASE: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '14px',
  transition: 'opacity 0.2s'
};

const LINK_STYLE: React.CSSProperties = {
  color: '#7dd3fc',
  textDecoration: 'underline',
  marginLeft: '4px'
};

export default function ConsentBanner(): React.ReactElement | null {
  const [visible, setVisible] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [prefs, setPrefs] = useState<ConsentPreferences | null>(null);

  useEffect(() => {
    setVisible(!hasConsentChoice());
  }, []);

  const handleAllow = (): void => {
    consentGrantAll();
    setVisible(false);
  };

  const handleDeny = (): void => {
    consentDenyAll();
    setVisible(false);
  };

  const handleManage = (): void => {
    setPrefs(getStoredConsent() || {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      timestamp: 0
    });
    setManageOpen(true);
  };

  const handleSaveManage = (): void => {
    if (prefs) {
      consentUpdate(prefs);
      setVisible(false);
      setManageOpen(false);
    }
  };

  const handleAcceptAllInManage = (): void => {
    consentGrantAll();
    setVisible(false);
    setManageOpen(false);
  };

  const handleRejectAllInManage = (): void => {
    consentDenyAll();
    setVisible(false);
    setManageOpen(false);
  };

  if (!visible && !manageOpen) return null;

  return (
    <>
      {visible && (
        <div style={BANNER_STYLE} role="dialog" aria-label="Çerez ve reklam tercihleri">
          <div style={MESSAGE_STYLE}>
            Bu sitede deneyiminizi iyileştirmek ve size ilgili reklamlar gösterebilmek için çerezler kullanıyoruz.
            Ayrıntılar için{' '}
            <a href="/gizlilik" style={LINK_STYLE} target="_blank" rel="noopener noreferrer">Gizlilik Politikası</a>
            ’mızı inceleyebilirsiniz.
          </div>
          <div style={BUTTON_GROUP_STYLE}>
            <button
              type="button"
              onClick={handleAllow}
              style={{
                ...BUTTON_BASE,
                background: '#0ea5e9',
                color: '#fff'
              }}
            >
              İzin ver
            </button>
            <button
              type="button"
              onClick={handleDeny}
              style={{
                ...BUTTON_BASE,
                background: 'transparent',
                color: '#94a3b8',
                border: '1px solid #64748b'
              }}
            >
              İzin verme
            </button>
            <button
              type="button"
              onClick={handleManage}
              style={{
                ...BUTTON_BASE,
                background: '#334155',
                color: '#e2e8f0',
                border: '1px solid #475569'
              }}
            >
              Seçenekleri yönet
            </button>
          </div>
        </div>
      )}

      {manageOpen && prefs && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            fontFamily: 'system-ui, sans-serif'
          }}
          role="dialog"
          aria-label="Rıza seçenekleri"
        >
          <div
            style={{
              background: '#1e293b',
              color: '#f1f5f9',
              borderRadius: 12,
              padding: 24,
              maxWidth: 420,
              width: '100%',
              boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
            }}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Tercihlerinizi yönetin</h3>
            <p style={{ margin: '0 0 16px', fontSize: 14, color: '#94a3b8' }}>
              Aşağıdan reklam ve analitik kullanımına izin verebilir veya reddedebilirsiniz.
            </p>

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={prefs.ad_storage === 'granted'}
                onChange={(e) => setPrefs({ ...prefs, ad_storage: e.target.checked ? 'granted' : 'denied', ad_user_data: e.target.checked ? 'granted' : 'denied', ad_personalization: e.target.checked ? 'granted' : 'denied' })}
              />
              <span>Reklamlar (kişiselleştirilmiş reklamlar)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <input
                type="checkbox"
                checked={prefs.analytics_storage === 'granted'}
                onChange={(e) => setPrefs({ ...prefs, analytics_storage: e.target.checked ? 'granted' : 'denied' })}
              />
              <span>Analitik (site kullanım istatistikleri)</span>
            </label>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleRejectAllInManage}
                style={{ ...BUTTON_BASE, background: '#334155', color: '#e2e8f0' }}
              >
                Tümünü reddet
              </button>
              <button
                type="button"
                onClick={handleAcceptAllInManage}
                style={{ ...BUTTON_BASE, background: '#0ea5e9', color: '#fff' }}
              >
                Tümünü kabul et
              </button>
              <button
                type="button"
                onClick={handleSaveManage}
                style={{ ...BUTTON_BASE, background: '#10b981', color: '#fff' }}
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
